import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  LayoutDashboard, 
  Shield, 
  Settings, 
  LogOut, 
  AlertTriangle,
  Users,
  FileText,
  Lock,
  Menu,
  X,
  CreditCard
} from 'lucide-react';
import { useState } from 'react';

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/partners', icon: Shield, label: 'Partners' },
    { to: '/cards', icon: CreditCard, label: 'Cards' },
    { to: '/audit', icon: FileText, label: 'Audit Log' },
    { to: '/settings', icon: Settings, label: 'Settings' },
  ];

  if (user?.is_admin) {
    navItems.push({ to: '/admin', icon: Users, label: 'Admin' });
  }

  if (user?.panic_mode) {
    navItems.splice(3, 0, { to: '/recovery', icon: Lock, label: 'Recovery', highlight: true });
  }

  return (
    <div className="min-h-screen bg-[#0B1C2D] flex">
      {/* Mobile menu button */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-[#112240] text-[#E6F1FF]"
        data-testid="mobile-menu-toggle"
      >
        {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-40
        w-64 bg-[#112240]/95 backdrop-blur-md
        border-r border-[#64FFDA]/10
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-[#64FFDA]/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#2BBBAD] to-[#64FFDA] flex items-center justify-center">
                <Shield className="w-6 h-6 text-[#0B1C2D]" />
              </div>
              <div>
                <h1 className="text-lg font-black text-[#E6F1FF] tracking-tight">PanicLock</h1>
                <p className="text-xs text-[#8892B0]">Security Layer</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) => `
                  flex items-center gap-3 px-4 py-3 rounded-lg
                  transition-colors duration-200
                  ${isActive 
                    ? 'bg-[#2BBBAD]/20 text-[#2BBBAD]' 
                    : item.highlight 
                      ? 'text-[#FF3B30] hover:bg-[#FF3B30]/10'
                      : 'text-[#8892B0] hover:bg-[#64FFDA]/10 hover:text-[#E6F1FF]'
                  }
                `}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon size={20} />
                <span className="font-medium">{item.label}</span>
                {item.highlight && (
                  <span className="ml-auto w-2 h-2 rounded-full bg-[#FF3B30] animate-pulse"></span>
                )}
              </NavLink>
            ))}
          </nav>

          {/* User Section */}
          <div className="p-4 border-t border-[#64FFDA]/10">
            {/* Status Banner */}
            <div className={`
              mb-4 p-3 rounded-lg flex items-center gap-2
              ${user?.panic_mode 
                ? 'bg-[#FF3B30]/20 border border-[#FF3B30]/30' 
                : 'bg-[#10B981]/20 border border-[#10B981]/30'
              }
            `}>
              <AlertTriangle size={16} className={user?.panic_mode ? 'text-[#FF3B30]' : 'text-[#10B981]'} />
              <span className={`text-sm font-medium ${user?.panic_mode ? 'text-[#FF3B30]' : 'text-[#10B981]'}`}>
                {user?.panic_mode ? 'PANIC MODE' : 'System Safe'}
              </span>
            </div>

            {/* User Info */}
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-[#1A2F4B] flex items-center justify-center">
                <span className="text-sm font-bold text-[#2BBBAD]">
                  {user?.full_name?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#E6F1FF] truncate">{user?.full_name}</p>
                <p className="text-xs text-[#8892B0] truncate">{user?.email}</p>
              </div>
            </div>

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg
                bg-transparent border border-[#64FFDA]/20 text-[#8892B0]
                hover:bg-[#FF3B30]/10 hover:border-[#FF3B30]/30 hover:text-[#FF3B30]
                transition-colors duration-200"
              data-testid="logout-button"
            >
              <LogOut size={18} />
              <span className="font-medium">Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="flex-1 min-h-screen overflow-x-hidden">
        <div className="p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
